const STATIC_PAGE_IDS = new Set([
  "/",
  "/auth/login",
  "/codex",
  "/extension",
  "/feedback",
  "/files",
  "/meetings",
  "/meetings/new",
  "/agents",
]);

const DYNAMIC_PAGE_IDS = [
  {
    pattern: /^\/chat\/[^/]+$/,
    pageId: "/chat/:id",
  },
  {
    pattern: /^\/codex\/[^/]+$/,
    pageId: "/codex/:conversationId",
  },
  {
    pattern: /^\/meetings\/[^/]+$/,
    pageId: "/meetings/:id",
  },
  {
    pattern: /^\/agents\/[^/]+$/,
    pageId: "/agents/:agentId",
  },
  {
    pattern: /^\/share\/meetings\/[^/]+$/,
    pageId: "/share/meetings/:token",
  },
] as const;

const PAGE_TITLES: Record<string, string> = {
  "/": "Sozo AI",
  "/auth/login": "ログイン | Sozo AI",
  "/chat/:id": "依頼 | Sozo AI",
  "/codex": "Codex | Sozo AI",
  "/codex/:conversationId": "Codex | Sozo AI",
  "/extension": "拡張機能 | Sozo AI",
  "/feedback": "フィードバック | Sozo AI",
  "/files": "ファイル | Sozo AI",
  "/meetings": "会議 | Sozo AI",
  "/meetings/:id": "会議 | Sozo AI",
  "/meetings/new": "会議を追加 | Sozo AI",
  "/agents": "エージェント | Sozo AI",
  "/agents/:agentId": "エージェント | Sozo AI",
  "/share/meetings/:token": "共有された会議 | Sozo AI",
};

// The stock RUM HTTP plugin records the complete request URL. Keep its
// automatic events to exact, static application routes; dynamic opaque IDs
// are correlated separately as API families with x-request-id.
const SAFE_STATIC_API_PATHS = [
  "/api/admin/audit-log/csv",
  "/api/admin/conversations",
  "/api/admin/conversations/dashboard",
  "/api/admin/credits/csv",
  "/api/admin/credits/pricing",
  "/api/admin/groups",
  "/api/admin/groups/user-groups",
  "/api/admin/meetings/reconcile",
  "/api/admin/recall/bots",
  "/api/admin/tenants",
  "/api/admin/users",
  "/api/admin/users/bulk",
  "/api/chat",
  "/api/client-errors",
  "/api/codex/conversations",
  "/api/codex/library",
  "/api/codex/references",
  "/api/codex/skills",
  "/api/codex/tools",
  "/api/codex/uploads",
  "/api/conversations",
  "/api/credits/balance",
  "/api/crm-initial-data/todays-actions",
  "/api/feedback",
  "/api/health",
  "/api/integrations/calendar",
  "/api/integrations/mazrica",
  "/api/mazrica/deals/suggestions",
  "/api/meetings",
  "/api/settings/summary-templates",
  "/api/settings/user-prompts",
  "/api/summary-templates",
  "/api/transcription-dictionary",
  "/api/user-prompts",
  "/api/user/groups",
  "/api/user/meeting-settings",
  "/api/user/organization-members",
] as const;
const SENSITIVE_RELATIVE_API_URLS = [
  /^\/api\/auth(?:\/|$)/,
  /^\/api\/public\/meetings(?:\/|$)/,
  /^\/api\/codex\/library\/[^/?#]+(?:\/|$)/,
  /^\/api\/codex\/conversations\/[^/?#]+\/workspace\/files(?:\/|$)/,
] as const;
const SAFE_API_FAMILIES = new Set([
  "admin",
  "chat",
  "client-errors",
  "codex",
  "conversations",
  "credits",
  "crm-initial-data",
  "feedback",
  "health",
  "integrations",
  "internal",
  "mazrica",
  "me",
  "meetings",
  "recall",
  "settings",
  "summary-templates",
  "transcription-dictionary",
  "user",
  "user-prompts",
]);

function normalizePathname(pathname: string) {
  const withoutQueryOrHash = pathname.split(/[?#]/, 1)[0] ?? "/";
  if (withoutQueryOrHash === "/") return "/";
  return withoutQueryOrHash.replace(/\/+$/, "") || "/";
}

/**
 * Returns only route templates known by the application. Unknown paths are
 * collapsed so user-provided slugs and public share tokens cannot become page
 * identifiers.
 */
export function toSafeRumPageId(pathname: string) {
  const normalized = normalizePathname(pathname);
  if (STATIC_PAGE_IDS.has(normalized)) return normalized;

  for (const route of DYNAMIC_PAGE_IDS) {
    if (route.pattern.test(normalized)) return route.pageId;
  }

  return "/_other";
}

export function getSafeRumPageTitle(pageId: string) {
  if (PAGE_TITLES[pageId]) return PAGE_TITLES[pageId];
  return "Sozo AI";
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * The RUM HTTP plugin records URLs verbatim. Limit it to same-origin API URLs
 * without a query or fragment so tokens and search parameters are never sent.
 */
export function buildSafeApiUrlPatterns(origin: string) {
  const normalizedOrigin = origin.replace(/\/+$/, "");
  const routes = SAFE_STATIC_API_PATHS.map(escapeRegExp).join("|");
  return [
    new RegExp(`^(?:${routes})$`),
    new RegExp(`^${escapeRegExp(normalizedOrigin)}(?:${routes})$`),
  ];
}

export function buildSensitiveApiUrlPatterns(origin: string) {
  const normalizedOrigin = origin.replace(/\/+$/, "");
  return SENSITIVE_RELATIVE_API_URLS.flatMap((pattern) => {
    const absolutePattern = pattern.source.replace(
      "^\\/api",
      `^${escapeRegExp(normalizedOrigin)}\\/api`,
    );
    return [pattern, new RegExp(absolutePattern)];
  });
}

export function isSafeApiUrl(url: string, origin: string) {
  try {
    const expectedOrigin = new URL(origin).origin;
    const parsed = new URL(url, expectedOrigin);
    if (
      parsed.origin !== expectedOrigin ||
      !parsed.pathname.startsWith("/api")
    ) {
      return false;
    }
    if (parsed.pathname !== "/api" && !parsed.pathname.startsWith("/api/")) {
      return false;
    }
    return !SENSITIVE_RELATIVE_API_URLS.some((pattern) =>
      pattern.test(parsed.pathname),
    );
  } catch {
    return false;
  }
}

export function toSafeRumApiFamily(url: string, origin: string) {
  if (!isSafeApiUrl(url, origin)) return "/api/_excluded";
  try {
    const parsed = new URL(url, origin);
    const family = parsed.pathname.split("/")[2];
    return family && SAFE_API_FAMILIES.has(family)
      ? `/api/${family}`
      : "/api/_other";
  } catch {
    return "/api/_other";
  }
}
